// ** React Imports
import { useEffect, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Get } from "../redux/api";

/**
 * ⭐⭐ WARN BEFORE THE SERVER SIGNS THEM OUT.
 *
 * App\Http\Middleware\IdleTimeout refuses a token untouched for 60 minutes (Yael's own number,
 * asked 2026-10-09 as "how long might you leave the screen without clicking anything while still
 * working?"). That check is the security control and lives on the server, where it cannot be
 * bypassed. This is only the courtesy on top.
 *
 * ⚠ WITHOUT THIS, THE FIRST SIGN OF THE TIMEOUT IS A LOST FORM. Yael fills in long sequence and
 * scheduling forms; being bounced to the login page mid-entry with no warning would be a worse
 * experience than the 24-hour token we are replacing.
 *
 * ⭐ ACTIVITY IS REAL INTERACTION, NOT A TIMER. The countdown resets on pointer, key, scroll and
 * touch - never on its own schedule - because a timer-driven "keep alive" would defeat the very
 * idle detection it is paired with.
 *
 * ⚠ The client clock is advisory. If this component is wrong, or the tab was asleep, the server
 * still refuses the request and the 401 interceptor explains why. Nothing here grants access.
 */

/**
 * ⚠ MIRRORS config/sanctum.php - `idle_timeout` and `idle_timeout_by_role`.
 *
 * ⭐ Drift here is survivable BY DESIGN, which is why it is duplicated rather than fetched. The
 * server is the only thing that decides whether a request is allowed; this just decides when to
 * show a courtesy dialog. If the two disagree the warning appears early or late and the 401
 * interceptor still explains what happened - nobody is let in or locked out by this file.
 *
 * GVMA gets longer because that account lives in the system all day (Mauricio, 2026-10-10).
 */
const IDLE_MINUTES_DEFAULT = 30;
const IDLE_MINUTES_BY_ROLE = { GVMA: 60 };

/**
 * ⭐ THE WARNING SCALES WITH THE LIMIT. A fixed 3 minutes is fine against 60 but absurd against
 * a short limit - at 10 minutes it would fire after 7 minutes of quiet, every time, and people
 * would learn to dismiss it without reading. That trains the exact reflex that makes a real
 * warning useless. Capped at 3 minutes, floored at a fifth of the limit.
 */
const warnBeforeMs = (limitMs) => Math.min(3 * 60 * 1000, limitMs * 0.2);

const TICK_MS = 15 * 1000;

const IdleWarning = () => {
  const { t } = useTranslation();
  const lastActivity = useRef(Date.now());
  const [secondsLeft, setSecondsLeft] = useState(null);

  // ⚠ Only for someone actually signed in. The visit page is reached by link, with no token,
  // and a care partner must never see a session dialog.
  const signedIn = (() => {
    try {
      const v = localStorage.getItem("accessToken");
      return !!v && v !== "null";
    } catch (e) { return false; }
  })();

  const idleLimitMs = (() => {
    try {
      const role = JSON.parse(localStorage.getItem("user") || "{}")?.role;
      return (IDLE_MINUTES_BY_ROLE[role] || IDLE_MINUTES_DEFAULT) * 60 * 1000;
    } catch (e) { return IDLE_MINUTES_DEFAULT * 60 * 1000; }
  })();

  useEffect(() => {
    if (!signedIn) return undefined;

    const bump = () => {
      lastActivity.current = Date.now();
      setSecondsLeft((s) => (s === null ? s : null));   // any interaction dismisses the warning
    };

    const events = ["mousedown", "keydown", "wheel", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const tick = setInterval(() => {
      const idleFor = Date.now() - lastActivity.current;
      const remaining = idleLimitMs - idleFor;
      setSecondsLeft(remaining <= warnBeforeMs(idleLimitMs) ? Math.max(0, Math.round(remaining / 1000)) : null);
    }, TICK_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(tick);
    };
  }, [signedIn, idleLimitMs]);

  // ⭐ "Stay signed in" has to touch the SERVER - the thing being timed is last_used_at, and only
  // a real authenticated request refreshes it. Resetting the local clock alone would leave the
  // dialog gone and the session still dying.
  const staySignedIn = async () => {
    lastActivity.current = Date.now();
    setSecondsLeft(null);
    try { await Get("/profile", false); } catch (e) { /* the interceptor owns a 401 */ }
  };

  if (!signedIn || secondsLeft === null) return null;

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <Modal isOpen backdrop="static" keyboard={false} centered className="modal-dialog-centered">
      <ModalBody className="text-center py-4">
        <h4 className="mb-1">{t("session.expiring_title")}</h4>
        <p className="mb-0">
          {t("session.expiring_body")} <strong>{mins}:{String(secs).padStart(2, "0")}</strong>
        </p>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-center">
        <Button color="primary" onClick={staySignedIn}>{t("session.stay_signed_in")}</Button>
      </ModalFooter>
    </Modal>
  );
};

export default IdleWarning;
